import { loadedScenarioData$ } from 'loadedScenarioData$';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { excludeNullAndUndefined } from 'streams/utils/streamOperators';
import { ScenarioComponentByIdItem } from './types';
import { mainNode } from './consts';
import { toByIdDictionary } from '@ardoq/common-helpers';
import { componentInterface } from '@ardoq/component-interface';

const nameSorter =
  (byId: Record<string, { name: string }>) => (a: string, b: string) => {
    const nameA = byId[a].name.toUpperCase();
    const nameB = byId[b].name.toUpperCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  };

export const scenarioRelated$ = loadedScenarioData$.pipe(
  map(({ scenarioData }) => scenarioData?.connected),
  excludeNullAndUndefined(),
  distinctUntilChanged(),
  map(({ workspaces, components, connectedComponents }) => {
    const byComponentId = toByIdDictionary(components);
    const byWorkspaceId = toByIdDictionary(workspaces);

    const flattenTree = components.reduce<Record<string, string[]>>(
      (acc, { _id, parent, rootWorkspace }) => {
        // if component has no parent, then the rootWorkspace will step in
        const parentId = parent || rootWorkspace;
        if (!acc[parentId]) {
          acc[parentId] = [];
        }
        acc[parentId].push(_id);

        if (!acc[_id]) {
          acc[_id] = [];
        }

        return acc;
      },
      {}
    );

    const sortedFlattenTree: Record<string, string[]> = {};
    for (const parentId in flattenTree) {
      const children = flattenTree[parentId];
      sortedFlattenTree[parentId] = children.sort(nameSorter(byComponentId));
    }

    const sortedRootIds = Object.keys(sortedFlattenTree)
      .filter(id => byWorkspaceId[id])
      .sort(nameSorter(byWorkspaceId));

    const connectedComponentsSet = new Set(connectedComponents);
    const byId: Record<string, ScenarioComponentByIdItem> = { mainNode };

    components.forEach(component => {
      byId[component._id] = {
        ...component,
        ghosted: !connectedComponentsSet.has(component._id),
        representationData: componentInterface.getRepresentationData(
          component._id
        ),
        cssClassNames: componentInterface.getCssClassNames(component._id),
      };
    });
    workspaces.forEach(workspace => {
      byId[workspace._id] = {
        ...workspace,
        ghosted: true,
        representationData: null,
        cssClassNames: null,
      };
    });

    return {
      flattenTree: {
        ...sortedFlattenTree,
        mainNode: sortedRootIds,
      } as Record<string, string[]>,
      rootIds: [mainNode._id],
      byId,
      connectedComponentsSet,
    };
  }),
  shareReplay({ refCount: true, bufferSize: 1 })
);
